import { initializeApp, type FirebaseApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import {
  getAuth,
  connectAuthEmulator,
  type Auth,
} from 'firebase/auth'
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore'
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions'
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage'

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

export const REGION = import.meta.env.VITE_FIREBASE_REGION || 'europe-west1'

export const app: FirebaseApp = initializeApp(config)

/**
 * Firestore med beständig lokal cache.
 *
 * Det här är hela offline-strategin för häftet: erbjudanden och företag som
 * användaren redan sett ligger kvar i webbläsaren, så att man kan visa ett
 * erbjudande i kassan även utan täckning. Själva inlösningen går däremot
 * genom en Cloud Function och kräver nät – annars skulle samma rabatt kunna
 * användas flera gånger i flygplansläge.
 */
export const db: Firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
})

/**
 * App Check – beviset på att anropet kommer från vår app.
 *
 * Firebase-konfigurationen ovanför är publik och kan inte vara något annat;
 * vem som helst kan läsa den ur bundlen och sedan anropa våra funktioner med
 * ett eget skript. Säkerhetsreglerna stoppar det som kräver inloggning, men
 * inte de öppna vägarna: createOrder skriver ett dokument och skickar ett
 * mejl vid varje anrop, och utan spärr blir föreningen en öppen mejlrelä.
 *
 * reCAPTCHA kör osynligt i bakgrunden och ger varje anrop en token som
 * servern kan verifiera. Ingen ruta att klicka i, inga bilder på trafikljus.
 *
 * Enterprise och inte klassiska v3, trots att v3 är gratis och enklare:
 * Firebase-konsolen märker v3 som äldre och hänvisar till Enterprise. Att
 * välja den variant konsolen avråder från är att bygga på något som kan
 * försvinna, och en bortplockad provider märks först när tokens slutar
 * accepteras – alltså när alla anrop börjar nekas.
 *
 * Enterprise har en fri kvot på 10 000 bedömningar i månaden. Med några
 * tusen användare per år och en timmes livslängd på token ligger föreningen
 * långt under den.
 *
 * Nyckeln sätts i VITE_RECAPTCHA_SITE_KEY. Saknas den händer ingenting alls –
 * det är avsiktligt, så att appen fungerar i emulatorn och så att ett glömt
 * miljövärde inte låser ute alla. Servern avgör själv om den kräver en token.
 */
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY

/*
 * Inte mot emulatorn.
 *
 * Emulatorerna verifierar ändå inga tokens, och reCAPTCHA vägrar utfärda
 * någon för localhost utan en registrerad felsökningstoken. Utan det här
 * villkoret skulle varje utvecklingssession fylla konsolen med fel som ser
 * ut som riktiga problem men inte är det – och den sortens brus gör att man
 * slutar läsa konsolen.
 *
 * Vill man ändå prova hela kedjan lokalt: registrera en felsökningstoken i
 * Firebase-konsolen och sätt VITE_APPCHECK_DEBUG_TOKEN i .env.local.
 */
const appCheckEnabled =
  Boolean(recaptchaSiteKey) &&
  (import.meta.env.VITE_USE_EMULATORS !== 'true' ||
    Boolean(import.meta.env.VITE_APPCHECK_DEBUG_TOKEN))

if (appCheckEnabled) {
  /*
   * Felsökningstoken för utveckling.
   *
   * Utan den skulle App Check försöka nå reCAPTCHA från localhost och
   * misslyckas. Token registreras en gång i Firebase-konsolen och gäller bara
   * den maskinen. Sätts aldrig i produktionsbygget.
   */
  if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
    ;(self as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN =
      import.meta.env.VITE_APPCHECK_DEBUG_TOKEN
  }

  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey as string),
    // Token förnyas av sig själv i bakgrunden. Utan det skulle den som har
    // appen öppen länge – en lagledare mitt i en avstämning – plötsligt nekas.
    isTokenAutoRefreshEnabled: true,
  })
}

export const auth: Auth = getAuth(app)
export const functions: Functions = getFunctions(app, REGION)
export const storage: FirebaseStorage = getStorage(app)

/** Lokal utveckling pratar med emulatorerna i stället för det skarpa projektet. */
const usingEmulators = import.meta.env.VITE_USE_EMULATORS === 'true'

if (usingEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
  connectStorageEmulator(storage, '127.0.0.1', 9199)
  // eslint-disable-next-line no-console
  console.info('[Lira] Kör mot lokala Firebase-emulatorer')
}
